import { AsyncPipe } from '@angular/common';
import {
  Component,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { AuthService as AuthApiService, LoginDto } from '@rezevent/api-types';
import { AuthService } from '../auth.service';
import { ControlsInterface } from '../utilities';

@Component({
  selector: 'core-page-login',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './page-login.component.html',
  styleUrl: './page-login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageLoginComponent {
  private authApi = inject(AuthApiService);
  private auth = inject(AuthService);
  private fb = inject(FormBuilder).nonNullable;

  // Branding inputs — each app passes its own values
  icon = input<string>('lock');
  appName = input<string>('Console');
  logoSrc = input<string>();
  logoInverseSrc = input<string>();
  tagline = input<string>('Admin Console');
  taglineSubtext = input<string>('Manage your platform from one place.');
  welcomeTitle = input<string>('Welcome back');
  welcomeSubtitle = input<string>('Sign in to your account to continue');
  submitLabel = input<string>('Sign in');
  emailError = input<string>('Enter a valid email address');
  passwordError = input<string>('Password must be at least 8 characters');

  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string>('');
  readonly year = new Date().getFullYear();

  ctrls: ControlsInterface<LoginDto> = {
    email: this.fb.control<string>('', [Validators.required, Validators.email]),
    password: this.fb.control<string>('', [
      Validators.required,
      Validators.minLength(8),
    ]),
  };

  fg = this.fb.group(this.ctrls);

  login() {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      return;
    }
    this.error$.next('');
    this.loading$.next(true);
    this.authApi.authControllerLogin(this.fg.getRawValue()).subscribe({
      next: response => {
        this.auth.authenticate(response.token, response.country);
        this.loading$.next(false);
      },
      error: err => {
        this.error$.next(err.message);
        this.loading$.next(false);
      },
    });
  }
}
